import { LogOut, UserCheck, X } from "lucide-react";
import { useImpersonation } from "@/lib/impersonation-context";
import type { PersonaBookmark } from "@dugads/shared";

function personaLabel(p: PersonaBookmark): string {
  return [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;
}

export function PersonaSwitcherSection({ collapsed }: { collapsed: boolean }) {
  const { personas, activePersona, isImpersonating, canImpersonate, startImpersonation, exitImpersonation, removePersona } =
    useImpersonation();

  if (!canImpersonate) return null;
  if (personas.length === 0 && collapsed) return null;

  return (
    <div
      data-component="persona-switcher"
      data-persona-count={personas.length}
      data-impersonating={isImpersonating ? "true" : "false"}
      className="border-b border-border p-2"
    >
      {!collapsed && (
        <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">Personas</p>
      )}
      {personas.length === 0 && !collapsed && (
        <p className="px-2 py-1 text-xs text-muted-foreground/60">
          Use the bookmark icon on user rows to add personas here.
        </p>
      )}
      <div className="flex flex-col gap-0.5">
        {personas.map((persona) => {
          const isActive = activePersona?.userId === persona.userId;
          const label = personaLabel(persona);
          return (
            <div
              key={persona.userId}
              data-persona-id={persona.userId}
              data-persona-email={persona.email}
              data-active={isActive ? "true" : "false"}
              className="group flex min-w-0 items-center"
            >
              <button
                type="button"
                data-action="switch-persona"
                title={isActive ? `Viewing as ${label}` : `Switch to ${label}`}
                onClick={() => void startImpersonation(persona)}
                className={`flex min-w-0 flex-1 items-center rounded-md text-sm transition-colors ${
                  collapsed ? "justify-center px-2 py-1.5" : "gap-2 px-2 py-1.5"
                } ${
                  isActive
                    ? "bg-amber-100 text-amber-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <UserCheck className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-amber-600" : ""}`} />
                {!collapsed && (
                  <span className="flex-1 truncate text-xs" title={persona.email}>
                    {label}
                  </span>
                )}
              </button>
              {!collapsed && (
                <button
                  type="button"
                  data-action="remove-persona"
                  onClick={() => removePersona(persona.userId)}
                  className="invisible mr-1 rounded p-0.5 text-muted-foreground hover:text-destructive group-hover:visible"
                  title="Remove persona"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      {isImpersonating && !collapsed && (
        <button
          type="button"
          data-action="exit-impersonation-sidebar"
          onClick={() => void exitImpersonation()}
          className="mt-1 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 transition-colors"
        >
          <LogOut className="h-3 w-3" />
          Exit impersonation
        </button>
      )}
    </div>
  );
}

export function MobilePersonaSwitcherSection() {
  const { personas, activePersona, isImpersonating, canImpersonate, startImpersonation, exitImpersonation, removePersona } =
    useImpersonation();

  if (!canImpersonate || personas.length === 0) return null;

  return (
    <div className="border-b border-border px-2 py-2">
      <p className="mb-1 px-2 text-xs font-medium text-muted-foreground">Personas</p>
      <div className="flex flex-col gap-0.5">
        {personas.map((persona) => {
          const isActive = activePersona?.userId === persona.userId;
          const label = personaLabel(persona);
          return (
            <div key={persona.userId} className="group flex min-w-0 items-center">
              <button
                type="button"
                onClick={() => void startImpersonation(persona)}
                className={`flex min-w-0 flex-1 items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors ${
                  isActive
                    ? "bg-amber-100 text-amber-900"
                    : "text-muted-foreground hover:bg-muted hover:text-foreground"
                }`}
              >
                <UserCheck className={`h-3.5 w-3.5 shrink-0 ${isActive ? "text-amber-600" : ""}`} />
                <span className="flex-1 truncate text-xs" title={persona.email}>{label}</span>
              </button>
              <button
                type="button"
                onClick={() => removePersona(persona.userId)}
                className="invisible mr-1 rounded p-0.5 text-muted-foreground hover:text-destructive group-hover:visible"
                title="Remove persona"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          );
        })}
      </div>
      {isImpersonating && (
        <button
          type="button"
          onClick={() => void exitImpersonation()}
          className="mt-1 flex w-full items-center gap-1.5 rounded-md px-2 py-1 text-xs text-amber-700 hover:bg-amber-100 transition-colors"
        >
          <LogOut className="h-3 w-3" />
          Exit impersonation
        </button>
      )}
    </div>
  );
}
